import {onMounted, ref, watch, Ref, computed, onUnmounted, getCurrentInstance} from "vue"
import {useConfigStore} from "../../app/store/config"
import {CrudFindOptions} from '@hc/graphql'
import {Logger} from '@hc/base'


export type UseSchemaObjectParams = CrudFindOptions & {
  collection?: string,
  id?: string|number
}


export function useSchemaObject<
  T=any,
  TInsert=any,
  TUpdate=any,
  TWhere=any,
  TOrder=any
>(
  initialParams: UseSchemaObjectParams) {

  const config = useConfigStore()
  const object = ref<T>(null) as Ref<T>
  const loading = ref<boolean>(false)
  const subscription = ref(null)
  const params = ref<UseSchemaObjectParams>({
    id: null,
    skipReturn: null,
    skipJoins: null,
    includeDefaultJoins: null,
    ignoreJoins: null,
    includeJoins: null,
    fields: null,
    apiToken: null,
    debug: null,
    collection: null,
    aggregate: null,
    ...initialParams
  }) as Ref<UseSchemaObjectParams>

  const collection = computed(()=>config.api.crudCollection(params.value.collection))
  const logger = new Logger(`SchemaObject`, () => ({collection: params.value?.collection, id: params.value?.id}))

  async function load(){
    if(!collection.value.canSelect) return
    if(!params.value.id) return
    loading.value = true
    try {
      logger.log("Load object", params.value)
      const serverObject = await collection.value.find(params.value.id, params.value)
      setObject(serverObject)
    } catch(e){
      logger.exception(e,"Error loading object %o: %s",params.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  function setObject(serverObject) {
    if(serverObject && serverObject.id != params.value.id){
      logger.warn(`Loaded object ${serverObject.id} does not match the id in the params (${params.value.id}), ignoring data`)
    } else {
      object.value = serverObject
    }
  }

  function clear(){
    object.value = null
    params.value.id = null
  }

  function subscribe() {
    logger.log('subscribe', params.value)
    subscription.value = collection.value.subscribeObject(params.value.id, params.value, {
      result: (result) => {
        setObject(result[0])
      },
      error: (e) => {
        logger.exception(e,"Error loading object %o: %s",params.value)
        throw e
      }
    })
  }

  function unsubscribe() {
    if(!subscription.value) return
    logger.log('unsubscribe', params.value)
    subscription.value?.unsubscribe()
  }

  async function update(data: TUpdate, id?: number|string, ): Promise<boolean>{
    id = id || params.value.id
    if(!id) {
      const e = new Error('Cannot update object without id')
      logger.error(e,"Cannot update object without id")
      throw e
    }
    loading.value = true
    try {
      logger.log("Update object", params.value, data)
      object.value = await collection.value.update(Number(id), data, params.value)
      return true
    } catch(e){
      logger.exception(e,"Error updating object",params.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function create(data: TInsert): Promise<number|null> {
    clear()
    loading.value = true
    try {
      logger.log("Create object", params.value, data)
      const result = await collection.value.create(data, {skipJoins: true, fields: ['id']})
      logger.log("Created object", result)
      params.value.id = result.id
      return result.id
    } catch(e){
      logger.exception(e,"Error creating object",params.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function destroy(id?: number|string): Promise<boolean> {
    id = id || params.value.id
    if(!id) {
      const e = new Error('Cannot delete object without id')
      logger.exception(e,"Cannot delete object without id")
      throw e
    }
    loading.value = true
    try {
      logger.log("Deleting object", id)
      const result = await collection.value.delete(Number(id), {skipReturn: true})
      logger.log("Deleted object", id)
      clear()
      return true
    } catch(e){
      logger.exception(e,"Error deleting object",id)
      throw e
    } finally {
      loading.value = false
    }
  }

  const onParamsChange = (value) => {
    //console.log("FETCH PARAMS CHANGED", JSON.stringify(value, null, 2))
    //logger.log("Params changed %o", params.value)
    object.value = null
    if(params.value.id){
      load()
    }
  }

  const onCollectionChange = () => {
    //console.log("FETCH COLLECTION CHANGED")
    clear()
  }

  watch(params, onParamsChange, {deep: true})
  watch(()=>params.value?.collection, onCollectionChange)

  if(getCurrentInstance()) {
    onMounted(async function(){
      if (params.value.id) await load()
    })
  }

  return {
    object,
    collection,
    params,
    loading,
    load,
    update,
    create,
    clear,
    destroy,
    subscribe,
    unsubscribe
  }
}